import React, { useContext, useEffect, useMemo, useState } from 'react';

import MapBoxMap from '@totem/components/common/mapBoxMap/MapBoxMap';
import buildingMarker from '@totem/components/common/svgIcons/buildingMarker.svg';
import BuildingSummaryContext from '@totem/components/portfolio/buildings/list/buildingSummary/buildingSummaryContext';
import { BuildingLocation } from '@totem/components/portfolio/buildings/list/buildingSummary/types';
import { isNotNull } from '@totem/utilities/common';

const DEFAULT_MARKER_SIZE = 32;

const BuildingsMapBoxMap = () => {
  const { reportData } = useContext(BuildingSummaryContext);
  const [buildings, setBuildings] = useState<BuildingLocation[]>([]);

  useEffect(() => {
    if (isNotNull(reportData)) {
      const buildingArray: BuildingLocation[] = [];
      if (isNotNull(reportData.locations)) {
        for (let idx = 0; idx < reportData.locations.length; idx++) {
          buildingArray.push(reportData.locations[idx]);
        }
        setBuildings(buildingArray);
      }
    }
  }, [reportData]);

  const buildingMarkers = useMemo(() => {
    if (isNotNull(buildings)) {
      return buildings
        .filter(({ location }) => location.lat !== 0 || location.lng !== 0)
        .map(({ id, location }) => ({
          id,
          lng: location.lng,
          lat: location.lat,
          iconUrl: buildingMarker,
        }));
    }
    return [];
  }, [buildings]);

  const mapConfig = useMemo((): { center: [number, number]; zoom: number } => {
    const valid = buildings.filter(
      ({ location }) => location.lat !== 0 || location.lng !== 0,
    );

    if (valid.length === 0) {
      return { center: [-98.5795, 39.8282], zoom: 2 };
    }

    const lats = valid.map(({ location }) => location.lat);
    const lngs = valid.map(({ location }) => location.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const center: [number, number] = [
      (minLng + maxLng) / 2,
      (minLat + maxLat) / 2,
    ];

    const maxSpan = Math.max(maxLat - minLat, maxLng - minLng);
    const zoom =
      maxSpan === 0
        ? 13
        : Math.max(1, Math.min(13, Math.round(Math.log2(360 / maxSpan))));

    return { center, zoom };
  }, [buildings]);

  return (
    <MapBoxMap
      markers={buildingMarkers}
      zoom={mapConfig.zoom}
      center={mapConfig.center}
      markerSize={DEFAULT_MARKER_SIZE}
    />
  );
};

export default BuildingsMapBoxMap;
